import type { RequestParams } from './types';

/** What the address is built from. */
export interface Address {
  baseUrl?: string;
  params?: RequestParams;
  url: string;
}

/**
 * Trims slashes off one end. Hand-written rather than `replace(/\/+$/u, '')`,
 * because that expression is only anchored at the end: on a run of slashes that
 * does NOT reach the end the engine retries from every position, which is
 * quadratic. Failing is the ordinary case, since most base URLs do not end in a
 * slash. MEASURED on `'https://a.nl/api' + '/'.repeat(n) + 'x'`: n=10000 took
 * 145 ms, n=40000 took 2347 ms, n=100000 took 14330 ms. The scan below is
 * linear and stays under a millisecond at every one of those sizes.
 *
 * The leading-slash side has no such trap — `^\/+` IS anchored, so it is linear
 * — but it is trimmed the same way here so the two halves of the join read as
 * one operation and no later edit reintroduces the pattern by symmetry.
 */
function trimSlashes(value: string, side: 'start' | 'end'): string {
  let start = 0;
  let end = value.length;
  if (side === 'start') while (start < end && value[start] === '/') start += 1;
  else while (end > start && value[end - 1] === '/') end -= 1;
  return value.slice(start, end);
}

/**
 * Joins `baseUrl` and `url` with exactly one slash. An absolute `url` wins.
 *
 * Concatenation, deliberately not `new URL(url, baseUrl)`: measured,
 * `new URL('/teams', 'https://a.nl/api')` yields `https://a.nl/teams` and drops
 * the `/api` segment.
 *
 * @remarks
 * ABSOLUTE MEANS WHAT RFC 3986 MEANS BY IT, which is wider than "http or
 * https": anything carrying a scheme wins, `mailto:` and `tel:` included. That
 * is deliberate, so no scheme list of ours blocks a caller who needs another
 * protocol. MEASURED against a strict scheme check over 13 inputs: it agrees
 * with `URL.canParse` on every one of them, so there is no rule that refuses
 * `a:b/c` while still accepting the schemes that matter (UR24).
 *
 * The edge that costs: a colon inside a PATH SEGMENT makes a scheme, so
 * `users/${ref}` with a `ref` of `a:b` is the absolute url `a:b` and the base is
 * skipped silently. Interpolating anything that may carry a colon -- an entity
 * reference, a mail address -- means encoding it first.
 *
 * An EMPTY url is refused rather than read as "the base itself", with or
 * without a `baseUrl` (UR22, UR23): without one it already threw, and an empty
 * url is nearly always an empty variable at the caller. Whoever wants the base
 * writes `url: '/'`.
 */
function join(baseUrl: string | undefined, url: string): string {
  // Refused HERE rather than left to `new URL`, which accepts `${base}/` and so
  // would answer a call the caller never meant to make. Whitespace only: `/` is
  // how a caller asks for the base deliberately, and `///` is that same intent
  // typed loosely (UR25).
  if (url.trim() === '') throw new TypeError('the url is empty');
  if (baseUrl === undefined || URL.canParse(url)) return url;
  return `${trimSlashes(baseUrl, 'end')}/${trimSlashes(url, 'start')}`;
}

/**
 * The one URL that goes on the wire.
 *
 * Throws the platform `TypeError` when the result is not an absolute URL — a
 * relative `url` without a `baseUrl`, an empty or whitespace-only string WITH
 * or without one, or something that is not
 * a URL at all. The caller wraps it; this function stays free of the error
 * vocabulary so it can be read as "what does the address become".
 */
export function resolveAddress(address: Address): string {
  const target = new URL(join(address.baseUrl, address.url));
  for (const [key, value] of Object.entries(address.params ?? {})) {
    // Undefined omits the key entirely. Everything else is a value, `''` included.
    if (value !== undefined) target.searchParams.set(key, String(value));
  }
  return target.toString();
}
